/**
 * PipelineHistoryStrip — recent months as clickable chips (month · rollup
 * status · dry-run marker). Clicking a chip loads that month into the tab.
 */

import React from 'react';
import { Eyebrow, StatusPill } from '../../ui';
import { PipelineRun } from '../../../services/adminApi';
import { overallPill } from './pipelineUtils';

interface PipelineHistoryStripProps {
  history: PipelineRun[];
  selectedMonth: string;
  onSelect: (month: string) => void;
  onViewProcessingHistory: () => void;
}

const PipelineHistoryStrip: React.FC<PipelineHistoryStripProps> = ({
  history,
  selectedMonth,
  onSelect,
  onViewProcessingHistory,
}) => {
  if (history.length === 0) return null;

  return (
    <div>
      <div style={styles.headerRow}>
        <Eyebrow>Previous months</Eyebrow>
        <button type="button" style={styles.link} onClick={onViewProcessingHistory}>
          View processing history →
        </button>
      </div>
      <div style={styles.strip}>
        {history.map((p) => {
          const pill = overallPill(p);
          const selected = p.month === selectedMonth;
          return (
            <button
              key={p.month}
              type="button"
              onClick={() => onSelect(p.month)}
              style={{
                ...styles.chip,
                ...(selected ? styles.chipSelected : {}),
              }}
              aria-pressed={selected}
            >
              <span style={styles.chipMonth}>{p.monthDisplay}</span>
              <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
              {p.dryRun && <span style={styles.dryRunTag}>dry run</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
  },
  link: {
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: 12.5,
    fontWeight: 600,
    color: 'var(--fg-link)',
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    boxShadow: 'none',
  },
  strip: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    boxShadow: 'var(--shadow-xs)',
    transition: 'border-color var(--dur-fast) var(--ease-standard), background var(--dur-fast) var(--ease-standard)',
  },
  chipSelected: {
    background: 'var(--accent-soft)',
    borderColor: 'var(--cg-midnight-200)',
  },
  chipMonth: {
    fontSize: 12.5,
    fontWeight: 600,
    color: 'var(--fg-1)',
    whiteSpace: 'nowrap',
  },
  dryRunTag: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--fg-3)',
  },
};

export default PipelineHistoryStrip;
